/**
 * Transport-level types shared by every endpoint.
 *
 * These intentionally mirror Spring conventions so the backend can be dropped in
 * without touching the UI:
 *  - `ApiResponse<T>`  -> your `@RestControllerAdvice` / response wrapper
 *  - `Page<T>`         -> `org.springframework.data.domain.Page`
 *  - `PageRequest`     -> `Pageable` query params (page, size, sort)
 */

/** Envelope returned by the mock API and (recommended) by the Spring controllers. */
export interface ApiResponse<T> {
  success: boolean;
  message: string;
  data: T;
  /** ISO-8601 instant, e.g. 2024-05-16T10:30:00Z */
  timestamp: string;
}

/** Shape of `org.springframework.data.domain.Page<T>` serialised to JSON. */
export interface Page<T> {
  content: T[];
  /** zero-based page index — same as Spring */
  number: number;
  size: number;
  totalElements: number;
  totalPages: number;
  first: boolean;
  last: boolean;
  empty: boolean;
}

export type SortDirection = 'asc' | 'desc';

/** Query params accepted by any paged endpoint (`Pageable` on the Spring side). */
export interface PageRequest {
  /** zero-based */
  page?: number;
  size?: number;
  /** `sort=createdAt,desc` */
  sortBy?: string;
  sortDir?: SortDirection;
}

/** Error body produced by the mock API; align your `@ExceptionHandler` with it. */
export interface ApiErrorBody {
  success: false;
  message: string;
  /** machine-readable code, e.g. QR_ALREADY_RECEIVED */
  errorCode?: string;
  status: number;
  path?: string;
  timestamp: string;
  /** field -> message, for bean-validation failures */
  fieldErrors?: Record<string, string>;
}

/** Normalised error every hook/component can rely on. */
export class ApiError extends Error {
  readonly status: number;
  readonly errorCode?: string;
  readonly fieldErrors?: Record<string, string>;

  constructor(message: string, status = 0, errorCode?: string, fieldErrors?: Record<string, string>) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.errorCode = errorCode;
    this.fieldErrors = fieldErrors;
  }
}

/** Generic `{ id, name }` pair for dropdowns. */
export interface OptionItem {
  id: number;
  name: string;
  code?: string;
}

export function emptyPage<T>(size = 10): Page<T> {
  return {
    content: [],
    number: 0,
    size,
    totalElements: 0,
    totalPages: 0,
    first: true,
    last: true,
    empty: true,
  };
}
